import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

// Create Theme Context
const ThemeContext = createContext();

// Custom hook to use theme
export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

// Theme Provider Component
export const ThemeProvider = ({ children }) => {
    // Initialize theme from localStorage or default to 'light'
    const [theme, setTheme] = useState(() => {
        const savedTheme = localStorage.getItem('theme');
        return savedTheme || 'light';
    });

    // Toggle between light and dark theme with optimized switching
    const toggleTheme = () => {
        // Add switching class to disable transitions
        document.documentElement.classList.add('theme-switching');
        
        const newTheme = theme === 'light' ? 'dark' : 'light';
        setTheme(newTheme);
        
        // Remove switching class after theme has been applied
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                document.documentElement.classList.remove('theme-switching');
            });
        });
    };

    // Save theme to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('theme', theme);
        
        // Batch DOM updates for better performance
        requestAnimationFrame(() => {
            // Apply theme class to document body
            document.body.className = theme === 'dark' ? 'dark-theme' : 'light-theme';
            
            // Also set data attribute for CSS targeting
            document.documentElement.dataset.theme = theme;
        });
    }, [theme]);

    // Apply initial theme on mount (no transition delay needed)
    useEffect(() => {
        document.body.className = theme === 'dark' ? 'dark-theme' : 'light-theme';
        document.documentElement.dataset.theme = theme;
    }, []);

    const value = useMemo(() => ({
        theme,
        toggleTheme,
        isDark: theme === 'dark',
        isLight: theme === 'light'
    }), [theme, toggleTheme]);

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

// PropTypes validation removed to avoid dependency requirement

export default ThemeContext;
